import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/shop';
import { formatMoney } from '../brand/brand';
import { usePageTitle } from '../a11y/usePageTitle';
import type { MyVoucher } from '../api/types';
import { ActivityList } from '../components/ActivityList';
import { ShareVoucher } from '../components/ShareVoucher';
import { OfflinePassPanel } from '../components/OfflinePassPanel';
import { keptPass } from '../lib/offlinePasses';

/**
 * The screen held up at the counter.
 *
 * This is the only place the voucher code is shown — the list endpoint deliberately
 * omits it, because the code plus its PIN is spendable and the list is the response
 * that gets cached and screenshotted most.
 */
export function VoucherDetail() {
  const { voucherId } = useParams<{ voucherId: string }>();

  const [voucher, setVoucher] = useState<MyVoucher | null>(null);
  const [error, setError] = useState(false);

  // null while loading, so the tab does not flash a title made of nothing.
  usePageTitle(voucher === null ? null : voucher.dealTitle);

  useEffect(() => {
    if (voucherId === undefined) return;

    let cancelled = false;

    api.voucher(voucherId)
      .then((result) => { if (!cancelled) setVoucher(result); })
      .catch(() => { if (!cancelled) setError(true); });

    return () => { cancelled = true; };
  }, [voucherId]);

  // No connection, but a pass was kept while online (TC-04): show that rather than
  // "not found". This is the moment the pass exists for.
  const offlineCopy = error && voucherId !== undefined ? keptPass(voucherId) : null;

  if (offlineCopy !== null) {
    return (
      <div className="stack narrow" style={{ gap: 18, marginBlock: 32 }}>
        <div className="panel stack center" style={{ gap: 6 }}>
          <p className="faint">{offlineCopy.businessName}</p>
          <h1>{offlineCopy.dealTitle}</h1>
        </div>
        <OfflinePassPanel voucher={{ id: offlineCopy.voucherId, dealTitle: offlineCopy.dealTitle, businessName: offlineCopy.businessName }} />
        <Link to="/vouchers" className="btn secondary block">Back to my vouchers</Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel narrow" style={{ marginTop: 40 }}>
        <h2>Voucher not found</h2>
        <Link to="/vouchers" className="btn" style={{ marginTop: 16 }}>Back to my vouchers</Link>
      </div>
    );
  }

  if (voucher === null) {
    return (
      <p className="muted" style={{ padding: '48px 0' }} role="status">
        Loading your voucher…
      </p>
    );
  }

  const usable = voucher.status === 'active' && voucher.balanceCents > 0;

  return (
    <div className="stack narrow" style={{ gap: 18, marginBlock: 32 }}>
      <div className="panel stack center" style={{ gap: 14 }}>
        <p className="faint">{voucher.businessName}</p>
        <h1>{voucher.dealTitle}</h1>

        <div>
          <p className="faint">Balance</p>
          <p className="price voucher-balance">{formatMoney(voucher.balanceCents)}</p>
        </div>

        {usable && voucher.code !== null && (
          <div className="stack" style={{ gap: 6 }}>
            <p className="faint">Show this code</p>

            {/*
              READ OUT ONE CHARACTER AT A TIME.
              A screen reader says "NF-DEMO-4821-9930" as words and run-together
              numbers — useless to somebody reading it aloud to a cashier. The
              aria-label spaces the characters so each is spoken separately; the
              printed text is unchanged.
            */}
            <p
              className="voucher-code"
              aria-label={voucher.code.split('').join(' ')}
            >
              {voucher.code}
            </p>
          </div>
        )}

        {!usable && (
          <p className="notice" role="status">
            {voucher.status === 'exhausted'
              ? 'This voucher has been fully used.'
              : `This voucher is ${voucher.status}.`}
          </p>
        )}
      </div>

      {usable && <PinPanel voucherId={voucher.id} hasPin={voucher.hasPin} />}

      {usable && <OfflinePassPanel voucher={voucher} />}

      {/* Below the PIN panel deliberately. A customer should have seen where
          their PIN lives before they are offered a way to send the code
          somewhere else — the two facts only make sense together. */}
      {usable && <ShareVoucher voucherId={voucher.id} />}

      {/* TC-01: for a customer without a smartphone. The PIN is never printed. */}
      {usable && voucher.code !== null && (
        <Link to={`/vouchers/${voucher.id}/print`} className="btn secondary block">
          Print a paper voucher
        </Link>
      )}

      <div className="panel stack" style={{ gap: 8 }}>
        <p className="muted">
          Valid until {new Date(voucher.validTo).toLocaleDateString()}
        </p>
        <p className="muted">
          {voucher.lockedToBranch === null
            ? `Spend it at any ${voucher.businessName} location.`
            : `Spend it at ${voucher.lockedToBranch}.`}
        </p>
        <p className="faint">
          Bought {new Date(voucher.purchasedAt).toLocaleDateString()}
        </p>
      </div>

      {/* Every movement on THIS voucher - the purchase, each visit, any refund -
          with a receipt behind each visit. A $100 voucher used five times shows
          five spends here, not a balance that moved in silence. */}
      <div className="panel stack" style={{ gap: 12 }}>
        <h2>Transactions</h2>
        <ActivityList voucherId={voucher.id} />
      </div>

      <Link to="/vouchers" className="btn secondary block">Back to my vouchers</Link>
    </div>
  );
}

/**
 * The PIN half of the counter credential. SOW TC-01.
 *
 * THE PIN IS NEVER DISPLAYED HERE by default, and that is the entire point of
 * splitting it from the code. The code is on this screen; if the PIN were too, a
 * photograph of one phone would be worth the balance and the split would buy
 * nothing. It arrives by email instead — a different channel, which is what makes
 * a photocopied paper voucher worthless.
 *
 * So this panel does two things and no more: it says a PIN will be asked for, and
 * it issues a new one for the customer who has lost theirs. The new PIN shows here
 * exactly once, because there is nowhere to read it back from — the server keeps a
 * hash and nothing else.
 */
function PinPanel({ voucherId, hasPin }: { voucherId: string; hasPin: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  async function reset() {
    setBusy(true);
    setFailed(false);

    try {
      const result = await api.resetVoucherPin(voucherId);
      setPin(result.pin);
      setConfirming(false);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  if (pin !== null) {
    return (
      <div className="panel stack" style={{ gap: 10 }}>
        <h2>Your new PIN</h2>

        {/*
          role="status", not "alert": it is the result of something the customer
          just asked for, so it should be announced without interrupting them.
          Spaced in the aria-label for the same reason as the voucher code —
          "4821" is otherwise read as "four thousand eight hundred and twenty-one",
          which is not what anyone says at a counter.
        */}
        <p className="voucher-code" role="status" aria-label={pin.split('').join(' ')}>
          {pin}
        </p>

        <p className="muted">
          Write it down now. We keep only a scrambled copy, so this is the one time
          it can be shown — if you lose it, get another new one.
        </p>
        <p className="faint">
          We have emailed it to you as well.
          {hasPin ? ' The old PIN no longer works.' : ' The counter will ask for it from now on.'}
        </p>
      </div>
    );
  }

  return (
    <div className="panel stack" style={{ gap: 10 }}>
      <h2>Your PIN</h2>

      {hasPin ? (
        <p className="muted">
          The counter asks for your 4-digit PIN before spending. We emailed it when
          you bought this — it is not shown here, so that a photo of this screen is
          not enough to spend your balance.
        </p>
      ) : (
        /*
          A voucher bought before PINs existed. Its code alone still spends the
          balance, so this is not a cosmetic difference — the offer to add one is
          the only way that customer can close the gap, and saying plainly what the
          gap is beats a reassuring silence.
        */
        <p className="muted">
          This voucher was bought before PINs were introduced, so its code alone can
          be spent. Adding a PIN means the counter asks for four digits as well —
          worth doing if you ever write the code down or print it.
        </p>
      )}

      {failed && (
        <p className="notice error" role="alert">
          Could not issue a new PIN just now. Please try again.
        </p>
      )}

      {confirming ? (
        <>
          {hasPin && (
            <p className="muted">
              This replaces your current PIN. The old one stops working straight away.
            </p>
          )}
          <div className="stack-h wrap" style={{ gap: 10 }}>
            <button type="button" className="btn" onClick={() => void reset()} disabled={busy}>
              {busy ? 'Issuing…' : hasPin ? 'Yes, issue a new PIN' : 'Yes, add a PIN'}
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              {hasPin ? 'Keep my PIN' : 'Not now'}
            </button>
          </div>
        </>
      ) : (
        /* Confirmed first: a stray tap here breaks the PIN a customer may be about
           to use, and they cannot undo it by tapping again. */
        <button
          type="button"
          className="btn secondary"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => { setConfirming(true); setFailed(false); }}
        >
          {hasPin ? 'I have lost my PIN' : 'Add a PIN'}
        </button>
      )}
    </div>
  );
}
